import { FC, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Atom, MenuIcon, ArrowLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuthForm } from "@/contexts/AuthFormProvider";
import { useMobileMenu } from "@/contexts/MobileMenuProvider";

interface PrivacyProps {
}

// Content component that uses the auth context
const PrivacyContent: FC = () => {
  const { showLoginForm, showRegisterForm } = useAuthForm();
  const { setShowMenu } = useMobileMenu();
  const [display, setDisplay] = useState(false);

  const scrollToSection = (sectionId: string) => {
    // If we're on privacy page, navigate back to home page with hash
    window.location.href = `/#${sectionId}`;
  };

  const navigateHome = () => {
    window.location.href = '/';
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setDisplay(true);
      } else {
        setDisplay(false);
      }
    }

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-black">
      {/* Header Navigation */}
      <motion.div 
        className="fixed top-0 left-0 right-0 flex justify-center items-center py-6"
        style={{ zIndex: 9999 }}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.2 }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-gray-950/90 to-transparent backdrop-blur-sm" style={{ zIndex: -1 }}></div>
        
        <div className="w-full max-w-7xl mx-auto px-6 flex justify-between items-center">
          {/* Logo Section */}
          <motion.div 
            className="flex items-center relative z-10 cursor-pointer"
            whileHover={{ scale: 1.05 }}
            transition={{ type: "spring", stiffness: 300 }}
            onClick={navigateHome}
          >
            <img
              src="/images/logo-light.png"
              alt="logo"
              width={120}
              height={50}
              className="brightness-110"
            />
          </motion.div>

          {/* Navigation Section */}
          <div className="flex justify-between items-center gap-8 relative z-10">
            {display ? (
              <>
                {/* Back to Home */}
                <motion.button 
                  className="text-gray-200 hover:text-blue-400 transition-all duration-300 flex items-center gap-2"
                  style={{ fontFamily: 'GilroyMedium' }}
                  onClick={navigateHome}
                  whileHover={{ scale: 1.05 }}
                >
                  <ArrowLeft className="w-4 h-4" />
                  Home
                </motion.button>

                {/* Divider */}
                <div className="w-px h-6 bg-gradient-to-b from-transparent via-gray-500 to-transparent"></div>

                {/* Features Link */}
                <motion.button 
                  className="text-gray-200 hover:text-blue-400 transition-all duration-300"
                  style={{ fontFamily: 'GilroyMedium' }}
                  onClick={() => scrollToSection('features')}
                  whileHover={{ scale: 1.05 }}
                >
                  Features
                </motion.button>

                {/* Divider */}
                <div className="w-px h-6 bg-gradient-to-b from-transparent via-gray-500 to-transparent"></div>

                {/* Pricing Link */}
                <motion.button 
                  className="text-gray-200 hover:text-blue-400 transition-all duration-300"
                  style={{ fontFamily: 'GilroyMedium' }}
                  onClick={() => scrollToSection('pricing')}
                  whileHover={{ scale: 1.05 }}
                >
                  Pricing
                </motion.button>

                {/* Divider */}
                <div className="w-px h-6 bg-gradient-to-b from-transparent via-gray-500 to-transparent"></div>

                {/* Beta Access Button */}
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.98 }}>
                  <Button
                    loading={false}
                    className="text-gray-200 bg-transparent hover:text-blue-400 transition-all duration-300"
                    style={{ fontFamily: 'GilroyMedium' }}
                    onClick={showLoginForm}
                  >
                    I have BETA Access
                  </Button>
                </motion.div>

                {/* Apply for Beta Button */}
                <motion.div 
                  whileHover={{ scale: 1.05 }} 
                  whileTap={{ scale: 0.98 }}
                >
                  <Button
                    loading={false}
                    icon={<Atom className="w-5 h-5" />}
                    onClick={showRegisterForm}
                    className="bg-gradient-to-r from-blue-600 to-blue-700 text-white font-bold hover:from-blue-700 hover:to-blue-800 px-6 py-3 rounded-xl transition-all duration-300"
                    style={{ fontFamily: 'GilroyMedium' }}
                  >
                    Sign Up
                  </Button>
                </motion.div>
              </>
            ) : (
              <motion.div 
                whileHover={{ scale: 1.1 }} 
                whileTap={{ scale: 0.9 }}
              >
                <Button 
                  loading={false} 
                  onClick={(_e) => setShowMenu(true)}
                  className="text-gray-200 hover:text-white transition-all duration-300"
                >
                  <MenuIcon className="w-6 h-6" />
                </Button>
              </motion.div>
            )}
          </div>
        </div>
      </motion.div>
      
      {/* Full-width bottom line separator */}
      <motion.div
        className="fixed top-20 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gray-600/50 to-transparent"
        style={{ zIndex: 9998 }}
        initial={{ opacity: 0, scaleX: 0 }}
        animate={{ opacity: 1, scaleX: 1 }}
        transition={{ duration: 1, delay: 0.8 }}
      />

      {/* Main Content */}
      <div className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Header with Background Effects */}
          <div className="relative mb-16">
            {/* Background Effects */}
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute top-1/4 left-1/6 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl" />
              <div className="absolute bottom-1/4 right-1/6 w-80 h-80 bg-purple-500/5 rounded-full blur-3xl" />
            </div>
            
            <motion.div
              className="relative z-10 text-center"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <motion.h1 
                className="text-5xl lg:text-6xl font-black text-white leading-tight mb-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.5 }}
              >
                Privacy
                <br />
                <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">
                  Policy
                </span>
              </motion.h1>
              
              <motion.p 
                className="text-lg text-gray-300 max-w-2xl mx-auto mb-8"
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
              >
                Your privacy matters to us. Learn how we protect and handle your data.
              </motion.p>
            </motion.div>
          </div>

          {/* Content Container */}
          <motion.div
            className="relative bg-gray-900/40 backdrop-blur-sm border border-gray-700/50 rounded-3xl overflow-hidden"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            {/* Glass morphism background effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-purple-500/5" />
            
            <div className="relative z-10 p-8 lg:p-12">
              <motion.div
                className="mb-8"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.7 }}
              >
                <p className="mb-6 text-sm text-gray-400">Last updated: 31 July 2025</p>
              </motion.div>
              
              <div className="prose max-w-none text-gray-300">
            
                <motion.p 
                  className="mb-8 text-lg leading-relaxed"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                >
                  Welcome to Shreds.AI ("Shreds," "we," "us" or "our"). We respect your privacy and are committed to protecting your personal data. 
                  This Privacy Policy explains how we collect, use, disclose and safeguard your information when you visit https://shreds.ai/
                  (the "Site") or use any services, applications or software we make available (collectively, the "Services").
                </motion.p>

                <motion.section 
                  className="mb-8"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.9 }}
                >
                  <h2 className="text-2xl font-semibold text-white mb-4 flex items-center">
                    <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-white font-bold text-sm">1</span>
                    </div>
                    Who We Are
                  </h2>
                  <p className="mb-4">
                    <strong className="text-blue-400">Data Controller</strong> – For purposes of the EU General Data Protection Regulation ("GDPR") and applicable U.S. privacy laws, 
                    ShredsAI Corp. (2093 PHILADELPHIA PIKE #5005, CLAYMONT, DE 19703, UNITED STATES) is the data controller of your personal information.
                  </p>
                </motion.section>

                <motion.section 
                  className="mb-8"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 1.0 }}
                >
                  <h2 className="text-2xl font-semibold text-white mb-4 flex items-center">
                    <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-white font-bold text-sm">2</span>
                    </div>
                    Scope
                  </h2>
                  <p className="mb-4">This Policy applies to information we process:</p>
                  <ul className="list-none mb-4 space-y-2">
                    <li className="flex items-start">
                      <ChevronRight className="w-5 h-5 text-blue-400 mr-2 mt-0.5 flex-shrink-0" />
                      When you browse or use the Site or Services.
                    </li>
                    <li className="flex items-start">
                      <ChevronRight className="w-5 h-5 text-blue-400 mr-2 mt-0.5 flex-shrink-0" />
                      When you create an account, purchase a subscription, upload repositories or tokens, or interact with us in any manner.
                    </li>
                    <li className="flex items-start">
                      <ChevronRight className="w-5 h-5 text-blue-400 mr-2 mt-0.5 flex-shrink-0" />
                      When we integrate with third‑party platforms (e.g., GitHub) at your request.
                    </li>
                  </ul>
                </motion.section>

                <motion.section 
                  className="mb-8"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 1.1 }}
                >
                  <h2 className="text-2xl font-semibold text-white mb-4 flex items-center">
                    <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-white font-bold text-sm">3</span>
                    </div>
                    The Information We Collect
                  </h2>
                  <div className="overflow-x-auto mb-4">
                    <table className="min-w-full border border-gray-600/50 bg-gray-800/30 backdrop-blur-sm rounded-xl">
                      <thead className="bg-gray-700/50">
                        <tr>
                          <th className="border border-gray-600/50 px-4 py-3 text-left text-white font-semibold">Category</th>
                          <th className="border border-gray-600/50 px-4 py-3 text-left text-white font-semibold">Data Elements</th>
                          <th className="border border-gray-600/50 px-4 py-3 text-left text-white font-semibold">Source</th>
                          <th className="border border-gray-600/50 px-4 py-3 text-left text-white font-semibold">Purpose</th>
                          <th className="border border-gray-600/50 px-4 py-3 text-left text-white font-semibold">Legal Basis*</th>
                        </tr>
                      </thead>
                      <tbody className="text-sm text-gray-300">
                        <tr className="hover:bg-gray-700/30 transition-colors">
                          <td className="border border-gray-600/50 px-4 py-3 font-medium text-blue-300">Account Data</td>
                          <td className="border border-gray-600/50 px-4 py-3">Name, email, company, password hash, locale</td>
                          <td className="border border-gray-600/50 px-4 py-3">You</td>
                          <td className="border border-gray-600/50 px-4 py-3">Provide Services; Secure login</td>
                          <td className="border border-gray-600/50 px-4 py-3">Contract; Legitimate Interest</td>
                        </tr>
                        <tr className="hover:bg-gray-700/30 transition-colors">
                          <td className="border border-gray-600/50 px-4 py-3 font-medium text-blue-300">Payment Data</td>
                          <td className="border border-gray-600/50 px-4 py-3">Billing address, VAT/Tax ID, last 4 digits of card (full card handled by Stripe)</td>
                          <td className="border border-gray-600/50 px-4 py-3">You / Stripe</td>
                          <td className="border border-gray-600/50 px-4 py-3">Process payments, bookkeeping</td>
                          <td className="border border-gray-600/50 px-4 py-3">Contract; Legal Obligation</td>
                        </tr>
                        <tr className="hover:bg-gray-700/30 transition-colors">
                          <td className="border border-gray-600/50 px-4 py-3 font-medium text-blue-300">Usage Data</td>
                          <td className="border border-gray-600/50 px-4 py-3">Log files, IP address, device type, browser, interactions, telemetry on AI generation</td>
                          <td className="border border-gray-600/50 px-4 py-3">Automated</td>
                          <td className="border border-gray-600/50 px-4 py-3">Service reliability, analytics, abuse prevention</td>
                          <td className="border border-gray-600/50 px-4 py-3">Legitimate Interest; Consent (for cookies/analytics)</td>
                        </tr>
                        <tr className="hover:bg-gray-700/30 transition-colors">
                          <td className="border border-gray-600/50 px-4 py-3 font-medium text-blue-300">Repository Metadata</td>
                          <td className="border border-gray-600/50 px-4 py-3">Repo name, branch, commit IDs, file paths (never full code unless explicitly generated)</td>
                          <td className="border border-gray-600/50 px-4 py-3">GitHub API (on your behalf)</td>
                          <td className="border border-gray-600/50 px-4 py-3">Generate software, track revisions</td>
                          <td className="border border-gray-600/50 px-4 py-3">Contract</td>
                        </tr>
                        <tr className="hover:bg-gray-700/30 transition-colors">
                          <td className="border border-gray-600/50 px-4 py-3 font-medium text-blue-300">GitHub Access Token</td>
                          <td className="border border-gray-600/50 px-4 py-3">Encrypted OAuth/token string (AES‑256 while at rest)</td>
                          <td className="border border-gray-600/50 px-4 py-3">You</td>
                          <td className="border border-gray-600/50 px-4 py-3">Authorized access to your repos</td>
                          <td className="border border-gray-600/50 px-4 py-3">Contract; Consent</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <p className="text-sm text-gray-400">* See Section 6 for detailed legal bases.</p>
                </motion.section>

                <motion.section 
                  className="mb-8"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 1.2 }}
                >
                  <h2 className="text-2xl font-semibold text-white mb-4 flex items-center">
                    <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-white font-bold text-sm">4</span>
                    </div>
                    No Sale of Personal Data & Ownership of Outputs
                  </h2>
                  <div className="bg-gradient-to-r from-green-500/10 to-blue-500/10 border border-green-400/30 rounded-xl p-6 mb-4">
                    <p className="mb-4 text-green-200 font-semibold">🔒 We do not sell or rent your personal data.</p>
                    <p className="mb-0 text-green-200">
                      ✨ <strong>All AI‑generated code and artifacts belong 100% to you.</strong> We claim no intellectual‑property rights over outputs 
                      the Service creates at your direction (except to the extent necessary to host and deliver them).
                    </p>
                  </div>
                </motion.section>

                <motion.section 
                  className="mb-8"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 1.3 }}
                >
                  <h2 className="text-2xl font-semibold text-white mb-4 flex items-center">
                    <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-white font-bold text-sm">5</span>
                    </div>
                    How We Use Your Information
                  </h2>
                  <div className="space-y-4">
                    <div className="flex items-start p-4 bg-gray-800/30 border border-gray-600/30 rounded-xl">
                      <div className="w-2 h-2 bg-blue-400 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                      <div>
                        <strong className="text-blue-300">Service Delivery</strong> – Authenticate you, generate and store software artifacts, provide support.
                      </div>
                    </div>
                    <div className="flex items-start p-4 bg-gray-800/30 border border-gray-600/30 rounded-xl">
                      <div className="w-2 h-2 bg-purple-400 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                      <div>
                        <strong className="text-purple-300">Security & Integrity</strong> – Detect fraud, prevent unauthorized access, encrypt sensitive tokens (AES‑256 at rest; TLS 1.3 in transit).
                      </div>
                    </div>
                    <div className="flex items-start p-4 bg-gray-800/30 border border-gray-600/30 rounded-xl">
                      <div className="w-2 h-2 bg-cyan-400 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                      <div>
                        <strong className="text-cyan-300">Improvement & Research</strong> – Aggregate, de‑identified analytics to improve models (never raw private code without your opt‑in).
                      </div>
                    </div>
                    <div className="flex items-start p-4 bg-gray-800/30 border border-gray-600/30 rounded-xl">
                      <div className="w-2 h-2 bg-green-400 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                      <div>
                        <strong className="text-green-300">Communications</strong> – Transactional emails, product updates, and—with your consent—marketing.
                      </div>
                    </div>
                    <div className="flex items-start p-4 bg-gray-800/30 border border-gray-600/30 rounded-xl">
                      <div className="w-2 h-2 bg-yellow-400 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                      <div>
                        <strong className="text-yellow-300">Legal & Compliance</strong> – Meet bookkeeping, export‑control, tax and regulatory obligations.
                      </div>
                    </div>
                  </div>
                </motion.section>

                <motion.section 
                  className="mb-8"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 1.35 }}
                >
                  <h2 className="text-2xl font-semibold text-white mb-4 flex items-center">
                    <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-white font-bold text-sm">6</span>
                    </div>
                    Legal Bases for Processing (GDPR/UK GDPR)
                  </h2>
                  <div className="overflow-x-auto mb-4">
                    <table className="min-w-full border border-gray-600/50 bg-gray-800/30 backdrop-blur-sm rounded-xl">
                      <thead className="bg-gray-700/50">
                        <tr>
                          <th className="border border-gray-600/50 px-4 py-3 text-left text-white font-semibold">Basis</th>
                          <th className="border border-gray-600/50 px-4 py-3 text-left text-white font-semibold">Activities</th>
                        </tr>
                      </thead>
                      <tbody className="text-sm text-gray-300">
                        <tr className="hover:bg-gray-700/30 transition-colors">
                          <td className="border border-gray-600/50 px-4 py-3 font-medium text-blue-300">Contract (Art. 6 (1)(b))</td>
                          <td className="border border-gray-600/50 px-4 py-3">Creating your account, generating software, processing payments, storing GitHub tokens at your request</td>
                        </tr>
                        <tr className="hover:bg-gray-700/30 transition-colors">
                          <td className="border border-gray-600/50 px-4 py-3 font-medium text-purple-300">Legitimate Interests (Art. 6 (1)(f))</td>
                          <td className="border border-gray-600/50 px-4 py-3">Security monitoring, fraud prevention, anonymized analytics, basic marketing to existing customers</td>
                        </tr>
                        <tr className="hover:bg-gray-700/30 transition-colors">
                          <td className="border border-gray-600/50 px-4 py-3 font-medium text-green-300">Consent (Art. 6 (1)(a))</td>
                          <td className="border border-gray-600/50 px-4 py-3">Optional cookies, promotional emails, model training on your private code (opt‑in only)</td>
                        </tr>
                        <tr className="hover:bg-gray-700/30 transition-colors">
                          <td className="border border-gray-600/50 px-4 py-3 font-medium text-yellow-300">Legal Obligation (Art. 6 (1)(c))</td>
                          <td className="border border-gray-600/50 px-4 py-3">Invoicing, tax, responding to lawful requests</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <p className="text-gray-400">Where we rely on legitimate interests, we balance our interests against yours and process only what is strictly necessary.</p>
                </motion.section>

                <motion.section 
                  className="mb-8"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 1.36 }}
                >
                  <h2 className="text-2xl font-semibold text-white mb-4 flex items-center">
                    <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-white font-bold text-sm">7</span>
                    </div>
                    California & U.S. State Privacy Rights
                  </h2>
                  <div className="bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border border-blue-400/30 rounded-xl p-6">
                    <p className="mb-4 text-blue-200">Residents of California, Colorado, Connecticut, Utah and Virginia have rights to:</p>
                    <ul className="list-none mb-4 space-y-2">
                      <li className="flex items-start">
                        <ChevronRight className="w-4 h-4 text-cyan-400 mr-2 mt-0.5 flex-shrink-0" />
                        Know/Access the categories and specific pieces of personal information we hold.
                      </li>
                      <li className="flex items-start">
                        <ChevronRight className="w-4 h-4 text-cyan-400 mr-2 mt-0.5 flex-shrink-0" />
                        Delete personal information (subject to exemptions).
                      </li>
                      <li className="flex items-start">
                        <ChevronRight className="w-4 h-4 text-cyan-400 mr-2 mt-0.5 flex-shrink-0" />
                        Correct inaccurate information.
                      </li>
                      <li className="flex items-start">
                        <ChevronRight className="w-4 h-4 text-cyan-400 mr-2 mt-0.5 flex-shrink-0" />
                        Opt Out of "sale" or "sharing" for cross‑context behavioral advertising (we do neither).
                      </li>
                      <li className="flex items-start">
                        <ChevronRight className="w-4 h-4 text-cyan-400 mr-2 mt-0.5 flex-shrink-0" />
                        Limit sensitive data use (we process no "sensitive personal information" as defined under the CPRA for cross‑context advertising).
                      </li>
                    </ul>
                    <p className="text-cyan-200">Exercise your rights at <a href="mailto:contact@shreds.ai" className="text-white hover:text-cyan-300 transition-colors">contact@shreds.ai</a> or via your account dashboard. We will verify your request consistent with state laws.</p>
                  </div>
                </motion.section>

                {/* Condensed sections for better UX */}
                <motion.div 
                  className="mb-8 space-y-6"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 1.37 }}
                >
                  {/* International Data Transfers */}
                  <div className="bg-gray-800/30 border border-gray-600/30 rounded-xl p-6">
                    <h3 className="text-xl font-semibold text-white mb-3 flex items-center">
                      <div className="w-6 h-6 bg-blue-500/30 rounded-lg flex items-center justify-center mr-2">
                        <span className="text-blue-400 text-xs">8</span>
                      </div>
                      International Data Transfers
                    </h3>
                    <p className="text-gray-300">We host primarily in France.</p>
                  </div>

                  {/* Data Retention */}
                  <div className="bg-gray-800/30 border border-gray-600/30 rounded-xl p-6">
                    <h3 className="text-xl font-semibold text-white mb-3 flex items-center">
                      <div className="w-6 h-6 bg-purple-500/30 rounded-lg flex items-center justify-center mr-2">
                        <span className="text-purple-400 text-xs">9</span>
                      </div>
                      Data Retention
                    </h3>
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between items-center p-3 bg-gray-700/30 rounded-lg">
                        <span className="text-gray-300 font-medium">Account data</span>
                        <span className="text-gray-400">Active + 3 years</span>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-gray-700/30 rounded-lg">
                        <span className="text-gray-300 font-medium">GitHub tokens</span>
                        <span className="text-gray-400">Until revoked or 90 days inactive</span>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-gray-700/30 rounded-lg">
                        <span className="text-gray-300 font-medium">Generated code</span>
                        <span className="text-gray-400">Until project deleted</span>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-gray-700/30 rounded-lg">
                        <span className="text-gray-300 font-medium">Logs & telemetry</span>
                        <span className="text-gray-400">12 months</span>
                      </div>
                    </div>
                    <p className="text-gray-400 text-sm mt-4">We will honor deletion requests sooner where applicable laws require.</p>
                  </div>
                </motion.div>

                <motion.section 
                  className="mb-8"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 1.38 }}
                >
                  <h2 className="text-2xl font-semibold text-white mb-4 flex items-center">
                    <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-white font-bold text-sm">10</span>
                    </div>
                    Your Privacy Rights (Global)
                  </h2>
                  <div className="overflow-x-auto mb-4">
                    <table className="min-w-full border border-gray-600/50 bg-gray-800/30 backdrop-blur-sm rounded-xl">
                      <thead className="bg-gray-700/50">
                        <tr>
                          <th className="border border-gray-600/50 px-4 py-3 text-left text-white font-semibold">Right</th>
                          <th className="border border-gray-600/50 px-4 py-3 text-left text-white font-semibold">How to Exercise</th>
                        </tr>
                      </thead>
                      <tbody className="text-sm text-gray-300">
                        <tr className="hover:bg-gray-700/30 transition-colors">
                          <td className="border border-gray-600/50 px-4 py-3 font-medium text-blue-300">Access & Portability</td>
                          <td className="border border-gray-600/50 px-4 py-3">Export your data from the dashboard or contact us</td>
                        </tr>
                        <tr className="hover:bg-gray-700/30 transition-colors">
                          <td className="border border-gray-600/50 px-4 py-3 font-medium text-purple-300">Rectification</td>
                          <td className="border border-gray-600/50 px-4 py-3">Update profile settings</td>
                        </tr>
                        <tr className="hover:bg-gray-700/30 transition-colors">
                          <td className="border border-gray-600/50 px-4 py-3 font-medium text-green-300">Erasure ("Right to be Forgotten")</td>
                          <td className="border border-gray-600/50 px-4 py-3">Delete your account or email contact@shreds.ai</td>
                        </tr>
                        <tr className="hover:bg-gray-700/30 transition-colors">
                          <td className="border border-gray-600/50 px-4 py-3 font-medium text-cyan-300">Restriction/Objection</td>
                          <td className="border border-gray-600/50 px-4 py-3">Disable analytics cookies; opt out of marketing</td>
                        </tr>
                        <tr className="hover:bg-gray-700/30 transition-colors">
                          <td className="border border-gray-600/50 px-4 py-3 font-medium text-yellow-300">Withdraw Consent</td>
                          <td className="border border-gray-600/50 px-4 py-3">Adjust cookie banner choices; revoke GitHub token</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <p className="text-gray-400">We will respond within 30 days (extendable to 90 days for complex requests, per GDPR).</p>
                </motion.section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">11. Security Measures</h2>
              <ul className="list-disc list-inside space-y-2">
                <li><strong>Encryption</strong> – TLS 1.3 in transit; AES‑256 at rest; separate KMS keys for tokens.</li>
                <li><strong>Zero‑Trust Architecture</strong> – Mandatory MFA for staff; least‑privilege IAM roles.</li>
                <li><strong>Backups & DR</strong> – Encrypted daily backups; 24‑hour RPO / 4‑hour RTO.</li>
                <li><strong>Secure Development Lifecycle (SDL)</strong> – Static & dynamic code scans; dependency monitoring.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">12. Third‑Party Services</h2>
              <p className="mb-4">We integrate with, or rely on, certain vendors who may process limited data:</p>
              <div className="overflow-x-auto mb-4">
                <table className="min-w-full border border-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="border border-gray-300 px-4 py-2 text-left">Vendor</th>
                      <th className="border border-gray-300 px-4 py-2 text-left">Purpose</th>
                      <th className="border border-gray-300 px-4 py-2 text-left">Safeguards</th>
                    </tr>
                  </thead>
                  <tbody className="text-sm">
                    <tr>
                      <td className="border border-gray-300 px-4 py-2">Amazon Web Services</td>
                      <td className="border border-gray-300 px-4 py-2">Hosting & storage</td>
                      <td className="border border-gray-300 px-4 py-2">ISO/IEC 27001, SOC 2 Type II</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-2">Stripe, Inc.</td>
                      <td className="border border-gray-300 px-4 py-2">Payment processing</td>
                      <td className="border border-gray-300 px-4 py-2">PCI‑DSS Level 1; tokenized card data</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-2">GitHub</td>
                      <td className="border border-gray-300 px-4 py-2">Source control integration</td>
                      <td className="border border-gray-300 px-4 py-2">OAuth 2.0; scoped tokens</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p>We execute Data Protection Agreements ("DPAs") and, where needed, SCCs with all vendors.</p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">13. Cookies & Similar Technologies</h2>
              <p className="mb-4">We use:</p>
              <ul className="list-disc list-inside mb-4 space-y-2">
                <li><strong>Strictly Necessary Cookies</strong> – Authentication, session management.</li>
                <li>No third‑party advertising or tracking cookies.</li>
              </ul>
              <p>You may manage preferences through our cookie banner or browser settings.</p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">14. Children's Privacy</h2>
              <p>
                Our Services are not directed to children under 16. We do not knowingly collect personal data from minors. 
                If you believe we have inadvertently received such information, contact us and we will delete it.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">15. Automated Decision‑Making & Profiling</h2>
              <p>
                We do not make decisions with legal or similarly significant effects solely by automated means. 
                Our AI generation suggests code; you remain in full control of acceptance and deployment.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">16. Changes to This Policy</h2>
              <p>
                We may update this Policy to reflect changes in technology or legal requirements. We will post the revised version 
                with the new "Last updated" date and, where material, notify you by email or in‑app banner at least 15 days before 
                changes take effect.
              </p>
            </section>

                <motion.section 
                  className="mb-8"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 1.4 }}
                >
                  <h2 className="text-2xl font-semibold text-white mb-4 flex items-center">
                    <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-white font-bold text-sm">17</span>
                    </div>
                    Contact Us
                  </h2>
                  <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-400/30 rounded-xl p-6 space-y-4">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center mr-3">
                        <span className="text-blue-400 text-sm">✉️</span>
                      </div>
                      <div>
                        <strong className="text-blue-300">Email:</strong> 
                        <a href="mailto:contact@shreds.ai" className="text-white hover:text-blue-300 transition-colors ml-2">
                          contact@shreds.ai
                        </a>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="w-8 h-8 bg-purple-500/20 rounded-lg flex items-center justify-center mr-3 mt-1">
                        <span className="text-purple-400 text-sm">📍</span>
                      </div>
                      <div>
                        <strong className="text-purple-300">Postal (USA):</strong> 
                        <span className="text-white ml-2">
                          ShredsAI Corp. 2093 PHILADELPHIA PIKE #5005, CLAYMONT, DE 19703, UNITED STATES
                        </span>
                      </div>
                    </div>
                  </div>
                </motion.section>

                <motion.section 
                  className="mb-8"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 1.5 }}
                >
                  <h2 className="text-2xl font-semibold text-white mb-4 flex items-center">
                    <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-white font-bold text-sm">18</span>
                    </div>
                    Effective Date
                  </h2>
                  <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-400/30 rounded-xl p-6">
                    <p className="mb-4 text-amber-200">
                      This Policy is effective as of <strong>31 July 2025</strong> and supersedes all prior versions.
                    </p>
                    <p className="text-orange-200">
                      By using Shreds.AI you acknowledge that you have read and understood this Privacy Policy.
                    </p>
                  </div>
                </motion.section>
              </div>
            </div>
          </motion.div>

          {/* Back to Home CTA */}
          <motion.div
            className="text-center mt-16"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.6 }}
          >
            <motion.button
              onClick={navigateHome}
              className="px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-xl hover:from-blue-500 hover:to-purple-500 transition-all duration-300 shadow-lg shadow-blue-500/25"
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="flex items-center space-x-2">
                <ArrowLeft className="w-5 h-5" />
                <span>Back to Home</span>
              </div>
            </motion.button>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

// Main Privacy component 
const Privacy: FC<PrivacyProps> = () => {
  return <PrivacyContent />;
};

export default Privacy;